/**
 * Farm & Acre Co - Cart Operation Queue
 *
 * SPACEY: Global cart operation queue to prevent concurrent modifications
 * Serializes all cart operations to prevent race conditions
 * Fixes Issue 1: No cart locking mechanism
 */

(function() {
  'use strict';

  // SPACEY: Debug system
  const QUEUE_DEBUG = false; // Set to true to enable debug logging
  const DEBUG_PREFIX = '[CART QUEUE DEBUG]';

  function debugLog(message, data) {
    if (QUEUE_DEBUG && data !== undefined) {
      console.log(DEBUG_PREFIX, message, data);
    } else if (QUEUE_DEBUG) {
      console.log(DEBUG_PREFIX, message);
    }
  }

  function debugError(message, error) {
    console.error(DEBUG_PREFIX, message, error);
  }

  // SPACEY: Queue state
  const operationQueue = [];
  let isProcessingQueue = false;

  /**
   * SPACEY: Queue a cart operation and execute when ready
   * @param {Function} operation - Async function to execute
   * @returns {Promise} Resolves when operation completes
   */
  function queueCartOperation(operation) {
    return new Promise((resolve, reject) => {
      debugLog('Queueing cart operation. Queue length:', operationQueue.length + 1);

      operationQueue.push({
        operation,
        resolve,
        reject,
        timestamp: Date.now()
      });

      processQueue();
    });
  }

  /**
   * SPACEY: Process operations from queue (FIFO)
   */
  async function processQueue() {
    if (isProcessingQueue) {
      debugLog('Queue already processing, skipping');
      return;
    }

    if (operationQueue.length === 0) {
      debugLog('Queue is empty, nothing to process');
      return;
    }

    isProcessingQueue = true;
    debugLog('=== STARTING QUEUE PROCESS ===');
    debugLog('Queue length:', operationQueue.length);

    const { operation, resolve, reject, timestamp } = operationQueue.shift();
    const waitTime = Date.now() - timestamp;

    debugLog('Processing operation (waited', waitTime, 'ms)');
    debugLog('Remaining queue length:', operationQueue.length);

    try {
      const result = await operation();
      debugLog('Operation completed successfully');
      resolve(result);
    } catch (error) {
      debugError('!!! OPERATION FAILED !!!', error);
      reject(error);
    } finally {
      isProcessingQueue = false;

      // Process next operation if any
      if (operationQueue.length > 0) {
        debugLog('More operations in queue, continuing...');
        processQueue();
      } else {
        debugLog('=== QUEUE EMPTY ===');
      }
    }
  }

  /**
   * SPACEY: Wrapped cart/update.js API call
   * @param {Object} updates - Object mapping line keys to quantities
   * @returns {Promise} Resolves with cart data
   */
  function cartUpdate(updates) {
    return queueCartOperation(async () => {
      debugLog('cart/update.js called with updates:', updates);

      const response = await fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates })
      });

      if (!response.ok) {
        throw new Error('Cart update failed: ' + response.statusText);
      }

      const data = await response.json();
      debugLog('cart/update.js completed', data);
      return data;
    });
  }

  /**
   * SPACEY: Wrapped cart/change.js API call
   * @param {string} key - Line item key
   * @param {number} quantity - New quantity (0 to remove)
   * @returns {Promise} Resolves with cart data
   */
  function cartChange(key, quantity) {
    return queueCartOperation(async () => {
      debugLog('cart/change.js called:', { key, quantity });

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: key, quantity })
      });

      if (!response.ok) {
        throw new Error('Cart change failed: ' + response.statusText);
      }

      const data = await response.json();
      debugLog('cart/change.js completed', data);
      return data;
    });
  }

  /**
   * SPACEY: Wrapped cart/add.js API call
   * @param {Object|Array} items - Items to add
   * @returns {Promise} Resolves with response data
   */
  function cartAdd(items) {
    return queueCartOperation(async () => {
      debugLog('cart/add.js called with items:', items);

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items })
      });

      if (!response.ok) {
        throw new Error('Cart add failed: ' + response.statusText);
      }

      const data = await response.json();
      debugLog('cart/add.js completed', data);
      return data;
    });
  }

  /**
   * SPACEY: Get current queue statistics
   * @returns {Object} Queue stats
   */
  function getQueueStats() {
    return {
      queueLength: operationQueue.length,
      isProcessing: isProcessingQueue,
      waitingOperations: operationQueue.length
    };
  }

  // Export for external use
  window.CartOperationQueue = {
    queueCartOperation,
    cartUpdate,
    cartChange,
    cartAdd,
    getQueueStats
  };

  debugLog('Cart operation queue initialized');

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function() {
      debugLog('DOM ready, cart queue active');
    });
  } else {
    debugLog('DOM already loaded, cart queue active');
  }

})();
